import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getLang, t } from "@/lib/i18n";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [lang, setL] = useState(getLang());
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const h = () => setL(getLang());
    window.addEventListener("lang-change", h);
    return () => window.removeEventListener("lang-change", h);
  }, []);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/mock-exam/history").then((r) => setHistory(r.data));
  }, []);

  if (!data) return <Layout><div className="max-w-6xl mx-auto px-6 py-16">Loading...</div></Layout>;

  const acc = data.questions_answered > 0 ? Math.round((data.correct_answers / data.questions_answered) * 100) : 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-12 fade-in">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// {user?.name}</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium mb-8">{t(lang, "dashboard")}</h1>

        <div className="grid sm:grid-cols-4 gap-4 mb-12 stagger" data-testid="stats-grid">
          {[
            { k: data.exams_taken, v: "exams taken" },
            { k: data.exams_passed, v: "exams passed" },
            { k: acc + "%", v: "accuracy" },
            { k: `${data.questions_answered}/${data.total_questions}`, v: "questions seen" },
          ].map((s) => (
            <div key={s.v} className="border border-border bg-card rounded-lg p-6" data-testid={`stat-${s.v.replace(/\s+/g, "-")}`}>
              <div className="font-serif text-3xl font-medium">{s.k}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.v}</div>
            </div>
          ))}
        </div>

        <h2 className="font-serif text-2xl font-medium mb-4">{t(lang, "progress")}</h2>
        <div className="grid md:grid-cols-2 gap-3 mb-12" data-testid="progress-by-category">
          {data.by_category.map((c) => {
            const Icon = Icons[c.icon] || Icons.BookOpen;
            const pct = c.total > 0 ? Math.round((c.answered / c.total) * 100) : 0;
            const name = c[`name_${lang}`] || c.name_pt;
            return (
              <div key={c.code} className="border border-border bg-card rounded-md p-4 cursor-pointer hover:border-[hsl(var(--accent))] transition-colors" onClick={() => nav(`/categories/${c.code}`)}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <div className="text-sm font-medium flex-1">{name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{c.answered}/{c.total}</div>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="font-serif text-2xl font-medium mb-4">Recent exams</h2>
        {history.length === 0 ? (
          <div className="text-muted-foreground text-sm border border-dashed border-border rounded-md p-8 text-center">
            No exams yet. <Button variant="link" onClick={() => nav("/categories")} data-testid="empty-start">Start one →</Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="exam-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-4 px-5 py-3 border border-border rounded-md bg-card">
                <div className={`w-2 h-2 rounded-full ${h.passed ? "bg-emerald-600" : "bg-red-600"}`} />
                <div className="flex-1 text-sm">{h.category_code} · {h.correct_count}/{h.total} correct</div>
                <div className="font-mono text-sm font-semibold">{h.score_pct}%</div>
                <div className="text-xs text-muted-foreground font-mono">{new Date(h.submitted_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
